import Img from 'components/Img';
import { LanguageContext } from 'providers/Language';
import { SheetContext } from 'providers/Sheet';
import { memo, useContext, useEffect, useRef, useState } from 'react';

type Props = {
  image: string;
  zoom: string;
  visible?: boolean;
  number: number;
  altText?: string;
  isAbTestVerticalScrollActive?: boolean;
  currentPage?: number;
  onLoad?: () => void;
};

const PageDefaultImage = ({
  image,
  zoom,
  visible = false,
  altText,
  number,
  isAbTestVerticalScrollActive,
  currentPage,
  onLoad,
}: Props) => {
  const zoomImageLoaded = useRef(false);
  const zoomImgRef = useRef<HTMLImageElement>(null);
  const [isZoomImageReady, setIsZoomImageReady] = useState(false);
  const [isNativeZoomed, setIsNativeZoomed] = useState(false);
  const [hasZoomBeenRequested, setHasZoomBeenRequested] = useState(false);
  const { cZoom, cCurrentPage, cSettledPage } = useContext(SheetContext);
  const { t } = useContext(LanguageContext);

  // In vertical scroll mode, use the settled page so intermediate flings never trigger downloads.
  // In horizontal flip mode, use currentPage.
  const activeSettledPage = isAbTestVerticalScrollActive
    ? cSettledPage ?? currentPage
    : cCurrentPage ?? currentPage;

  // 1. Settled Landing Page: Loads immediately with high priority
  const isCurrentPage =
    !isAbTestVerticalScrollActive ||
    (activeSettledPage !== undefined && number === activeSettledPage);

  // 2. Next Page Forward (N+1): Preloaded immediately in parallel (0ms delay) so it's ready in advance
  const isNextPageForward =
    isAbTestVerticalScrollActive &&
    activeSettledPage !== undefined &&
    number === activeSettledPage + 1;

  // 3. Previous Page (N-1): Preloaded for seamless backward navigation
  const isPreviousPage =
    isAbTestVerticalScrollActive &&
    activeSettledPage !== undefined &&
    number === activeSettledPage - 1;

  const [isInView, setIsInView] = useState(isCurrentPage || isNextPageForward || isPreviousPage);
  const [imageState, setImageState] = useState(image);

  // Preload Strategy:
  // - Current page (N) loads immediately with high fetch priority.
  // - Next page forward (N+1) preloads in parallel with normal priority (0ms delay) so it is ready before the user scrolls to it.
  // - Previous page (N-1) preloads in parallel with low priority.
  useEffect(() => {
    if (isInView) return;

    if (isCurrentPage || isNextPageForward || isPreviousPage) {
      setIsInView(true);
    }
  }, [isCurrentPage, isNextPageForward, isPreviousPage, isInView]);

  // Track native browser pinch zoom on mobile only for the active page
  useEffect(() => {
    if (!isAbTestVerticalScrollActive || !isCurrentPage) return;

    const checkIsZoomed = () => (window.visualViewport?.scale ?? 1) > 1.01;

    const handleZoomCheck = () => {
      const zoomed = checkIsZoomed();
      setIsNativeZoomed(zoomed);
      if (zoomed) {
        setHasZoomBeenRequested(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches && e.touches.length >= 2) {
        setIsNativeZoomed(true);
        setHasZoomBeenRequested(true);
      }
    };

    handleZoomCheck();

    const { visualViewport } = window;
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleZoomCheck);
      visualViewport.addEventListener('scroll', handleZoomCheck);
    }
    window.addEventListener('resize', handleZoomCheck);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('gesturestart', handleZoomCheck as EventListener, { passive: true });
    window.addEventListener('gesturechange', handleZoomCheck as EventListener, { passive: true });

    return () => {
      if (visualViewport) {
        visualViewport.removeEventListener('resize', handleZoomCheck);
        visualViewport.removeEventListener('scroll', handleZoomCheck);
      }
      window.removeEventListener('resize', handleZoomCheck);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('gesturestart', handleZoomCheck as EventListener);
      window.removeEventListener('gesturechange', handleZoomCheck as EventListener);
    };
  }, [isAbTestVerticalScrollActive, isCurrentPage]);

  useEffect(() => {
    if (zoomImgRef.current && zoomImgRef.current.complete && zoomImgRef.current.naturalWidth > 0) {
      setIsZoomImageReady(true);
    }
  }, [hasZoomBeenRequested, isInView, isNativeZoomed, isCurrentPage]);

  useEffect(() => {
    if (!isAbTestVerticalScrollActive) {
      if ((visible || isInView) && cZoom > 1 && !zoomImageLoaded.current) {
        setImageState(zoom);
        zoomImageLoaded.current = true;
      } else if (cZoom === 1 && imageState !== image) {
        setImageState(image);
        zoomImageLoaded.current = false;
      }
    }
  }, [cZoom, visible, isInView, zoom, image, imageState, isAbTestVerticalScrollActive]);

  const alt = altText ?? `${t('page')} ${number}`;

  if (!isAbTestVerticalScrollActive) {
    return <Img src={imageState} alt={alt} showLoading={cZoom === 1} disableLazyLoading />;
  }

  let fetchPriority: 'high' | 'auto' | 'low' = 'low';

  if (isCurrentPage) {
    fetchPriority = 'high';
  } else if (isNextPageForward) {
    fetchPriority = 'auto';
  }

  return (
    <div className="page-default__image-wrapper" style={{ width: '100%', height: '100%' }}>
      <Img
        className="page-default__image"
        src={isInView ? image : ''}
        alt={alt}
        showLoading={cZoom === 1}
        useSkeleton
        fetchPriority={fetchPriority}
        onLoad={onLoad}
      />
      {isInView && isCurrentPage && hasZoomBeenRequested && (
        <img
          ref={zoomImgRef}
          src={zoom}
          alt={alt}
          aria-hidden="true"
          className="page-default__image page-default__image--zoom"
          decoding="async"
          {...({ fetchpriority: 'high' } as any)}
          onLoad={() => setIsZoomImageReady(true)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isNativeZoomed && isZoomImageReady ? 1 : 0,
            transition: 'opacity 0.25s ease-in-out',
            willChange: 'opacity',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}
    </div>
  );
};

export default memo(PageDefaultImage);
